import { FiSend } from "react-icons/fi";
import { useEffect, useState } from "react";
import { Text } from "./Text";

export type HeaderExtras = {
  primaryLabel?: string;
  secondaryLabel?: string;
  indicatorTone?: "pink" | "green" | "muted";
  revealCenterOnScroll?: boolean;
  share?: {
    url?: string;
  };
};

type Props = {
  className?: string;
  extras?: HeaderExtras;
};

const INDICATOR_CLASSES: Record<
  NonNullable<HeaderExtras["indicatorTone"]>,
  string
> = {
  pink: "bg-butter-pink",
  green: "bg-butter-green",
  muted: "bg-butter-green/40",
};

export function Header({ className = "max-w-[1000px]", extras = {} }: Props) {
  const {
    primaryLabel,
    secondaryLabel,
    indicatorTone = "pink",
    revealCenterOnScroll = false,
    share,
  } = extras;
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    if (!revealCenterOnScroll) {
      setHasScrolled(false);
      return;
    }
    const update = () => setHasScrolled(window.scrollY > 8);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, [revealCenterOnScroll]);

  const showCenter =
    Boolean(primaryLabel || secondaryLabel) &&
    (!revealCenterOnScroll || hasScrolled);

  const handleShare = async () => {
    if (!share) return;
    const url =
      share.url ?? (typeof window !== "undefined" ? window.location.href : "");
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ url });
        return;
      }
    } catch {
      // fall through to clipboard
    }
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // no-op
      }
    }
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-butter-cream-light/60 backdrop-blur-md border-b border-butter-cream">
      <div className={`w-full ${className} mx-auto px-5`}>
      <div className="flex justify-between items-center gap-4 py-3">
      <a href="https://joinbutter.co" className="flex-shrink-0">
        <svg
          width="80"
          height="30"
          viewBox="0 0 428 125"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22.46 0.00360371C20.2342 0.00360371 18.5658 1.48696 18.1948 3.71238L0.0175874 119.065C-0.167894 121.104 1.12936 122.775 3.16966 122.775H18.009C20.6057 122.775 21.9048 121.29 22.2757 119.065L24.1304 106.826C28.5819 78.4515 41.5649 66.2118 52.6938 66.2118C62.5243 66.2118 65.3074 75.6706 62.7106 86.9831C60.4848 96.812 54.5486 106.27 45.6455 106.27C41.1939 106.27 38.9687 105.899 37.2996 105.343C34.7028 104.601 33.0332 104.971 32.2913 106.826L28.5819 115.542C27.469 118.138 28.3964 119.622 32.4771 121.291C35.4448 122.589 42.6794 125 55.1066 125C72.1504 125 85.7833 120.455 96.5063 111.116C99.0397 119.426 105.91 125 117.622 125C128.009 125 136.171 120.55 142.477 114.059C146.001 120.735 152.864 125 162.138 125C171.783 125 179.516 120.277 185.43 113.657C188.93 120.544 195.859 125 205.672 125C215.204 125 222.919 120.215 228.841 113.528C232.32 120.486 239.271 125 249.144 125C260.586 125 269.411 118.107 275.659 109.3C282.161 118.96 293.779 125 307.639 125C329.23 125 347.438 110.525 355.832 92.0606C356.303 91.8156 356.669 91.3348 356.917 90.5066C359.7 81.6049 361.74 71.4059 362.853 61.0207L370.086 64.3579C373.054 65.656 373.981 67.1393 373.054 70.8483C370.643 81.419 367.675 88.8378 366.191 97.554C363.965 112.019 374.908 125 390.489 125C410.706 125 423.689 103.488 427.77 87.9106C428.326 85.8706 427.957 84.5721 425.731 83.8303L418.496 81.6044C416.271 80.8626 414.972 81.6049 414.23 83.8303C411.448 91.4338 404.028 106.27 395.125 106.27C390.859 106.27 387.892 102.747 389.933 93.8447C392.344 82.9031 396.239 69.5498 396.61 68.0662C397.908 61.9463 397.537 58.609 391.416 55.8272C382.142 51.7473 370.827 48.2239 365.448 44.3293C365.819 37.0967 362.11 30.9768 354.876 31.7186C346.529 32.4604 343.006 41.3628 347.457 52.3044C348.532 58.5763 347.865 72.8031 343.971 83.9619C343.743 84.3599 343.507 84.7998 343.251 85.3132C336.202 99.7784 323.034 105.899 311.72 105.899C303.744 105.899 298.736 102.747 296.139 97.74C312.276 98.1109 334.534 90.506 338.8 72.7025C342.51 57.681 331.009 46.3693 314.316 46.3693C295.767 46.3693 275.364 60.2773 270.913 82.346C270.488 84.3896 270.238 86.39 270.126 88.3457C266.908 95.8956 261.417 106.27 254.892 106.27C249.142 106.27 248.586 100.335 250.07 90.5061L254.509 62.4982H264.525C267.122 62.4982 268.605 61.0149 268.791 58.7894L270.275 50.0732C270.646 48.0333 269.162 46.3645 267.122 46.3645H257.105L259.517 30.6003C259.888 28.5604 258.404 26.8915 256.363 26.8915H241.526C239.3 26.8915 237.63 28.3749 237.259 30.6003L234.663 46.3645H228.355C227.395 46.3645 226.516 46.6538 225.798 47.1462C225.235 46.6636 224.496 46.3645 223.649 46.3645H213.632L216.044 30.6003C216.415 28.5604 214.931 26.8915 212.891 26.8915H198.053C195.827 26.8915 194.157 28.3749 193.786 30.6003L191.19 46.3645H184.883C182.657 46.3645 180.803 47.8478 180.618 50.0732L179.133 58.7894C178.947 60.8294 180.246 62.4982 182.286 62.4982H188.778L185.446 83.0389C185.329 83.2744 185.221 83.5337 185.124 83.8252C182.528 90.6869 176.221 106.265 167.689 106.265C162.496 106.265 161.938 100.33 163.422 90.5007L169.358 52.2986C169.729 50.2586 168.43 48.5883 166.204 48.5883H151.365C149.139 48.5883 147.471 50.0731 147.1 52.2986L142.833 79.7445C140.793 92.3552 134.116 106.265 124.842 106.265C118.721 106.265 117.423 100.33 119.092 90.5007L125.028 52.2986C125.399 50.2586 124.1 48.5883 121.874 48.5883H107.035C104.809 48.5883 103.141 50.0731 102.77 52.2986L98.0046 83.196C97.9152 83.3915 97.8292 83.5936 97.7523 83.8244C95.341 90.3152 92.7453 94.2097 89.7773 97.3623C84.3983 102.926 76.9791 107.006 72.1565 107.006C76.9791 104.224 85.6967 94.5798 86.8096 79.5583C88.2935 59.5295 75.1235 46.3633 58.2446 46.3633C46.0028 46.3633 36.7295 53.41 31.7215 58.6023L40.4392 3.70877C40.8101 1.66881 39.5113 0 37.2856 0L22.46 0.00360371ZM223.724 61.7588C224.257 62.2179 224.951 62.5003 225.775 62.5003H232.266L228.981 82.758C228.802 83.0656 228.638 83.4125 228.487 83.8276C225.891 90.6893 219.4 106.267 211.424 106.267C205.674 106.267 205.118 100.332 206.601 90.503L211.04 62.5031H221.057C222.147 62.5031 223.028 62.2296 223.712 61.7617L223.724 61.7588ZM313.762 65.4676C317.471 65.4676 319.511 67.6936 319.511 70.6608C319.511 78.2643 307.084 86.0524 294.471 86.6088C294.471 86.4233 294.472 86.2382 294.657 85.8673V85.3101C296.698 73.9976 306.713 65.4676 313.762 65.4676Z"
            fill="#F3564F"
          />
        </svg>
      </a>

      {showCenter && (
        <div className="hidden sm:flex flex-1 justify-center min-w-0 transition-opacity duration-200">
          <div className="inline-flex items-center gap-2 min-w-0">
            <span
              aria-hidden
              className={`inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${INDICATOR_CLASSES[indicatorTone]}`}
            />
            {primaryLabel && (
              <Text
                as="span"
                variant="secondary"
                size="sm"
                weight="semibold"
                className="truncate"
              >
                {primaryLabel}
              </Text>
            )}
            {primaryLabel && secondaryLabel && (
              <span aria-hidden className="text-butter-green">
                ·
              </span>
            )}
            {secondaryLabel && (
              <Text
                as="span"
                variant="secondary"
                size="sm"
                className="truncate"
              >
                {secondaryLabel}
              </Text>
            )}
          </div>
        </div>
      )}

      {share && (
        <button
          type="button"
          onClick={handleShare}
          className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-butter-green transition-transform hover:scale-[1.03] focus-visible:outline-2 focus-visible:outline-butter-green"
        >
          Share
          <FiSend size={14} aria-hidden />
        </button>
      )}
      </div>
      {showCenter && (
        <div className="sm:hidden flex items-center justify-center gap-2 pb-2.5 min-w-0 transition-opacity duration-200">
          <span
            aria-hidden
            className={`inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${INDICATOR_CLASSES[indicatorTone]}`}
          />
          {primaryLabel && (
            <Text
              as="span"
              variant="secondary"
              size="sm"
              weight="semibold"
              className="truncate"
            >
              {primaryLabel}
            </Text>
          )}
          {primaryLabel && secondaryLabel && (
            <span aria-hidden className="text-butter-green">
              ·
            </span>
          )}
          {secondaryLabel && (
            <Text
              as="span"
              variant="secondary"
              size="sm"
              className="truncate"
            >
              {secondaryLabel}
            </Text>
          )}
        </div>
      )}
      </div>
    </header>
  );
}
